import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessagesSquare, X } from 'lucide-react';
import api from '../lib/api';

// Polls for new inbound website-chat messages and fires a browser notification
// + a short sound anywhere in the CRM. Mounted once inside the authenticated
// Layout. Sound needs a prior user gesture (browser autoplay policy) — we prime
// an AudioContext on the first click.
let audioCtx = null;

const primeAudio = () => {
  if (audioCtx) return;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (Ctx) audioCtx = new Ctx();
  } catch { /* noop */ }
};

const playDing = () => {
  if (!audioCtx) return;
  try {
    if (audioCtx.state === 'suspended') audioCtx.resume();
    const now = audioCtx.currentTime;
    // Louder, more attention-grabbing 3-tone chime.
    [880, 1174, 1568].forEach((freq, i) => {
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.type = 'triangle';
      osc.frequency.value = freq;
      const t = now + i * 0.16;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.7, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.45);
      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.start(t);
      osc.stop(t + 0.47);
    });
  } catch { /* noop */ }
};

export const WebsiteChatNotifier = () => {
  const navigate = useNavigate();
  const lastId = useRef(undefined); // undefined = not yet baselined
  const unseen = useRef(0);
  const hasUnread = useRef(false);
  const flashTimer = useRef(null);
  const originalTitle = useRef(typeof document !== 'undefined' ? document.title : '');
  const [popup, setPopup] = useState(null); // {preview} — persistent until clicked

  const stopFlash = () => {
    if (flashTimer.current) { clearInterval(flashTimer.current); flashTimer.current = null; }
    unseen.current = 0;
    if (originalTitle.current) document.title = originalTitle.current;
  };

  const startFlash = () => {
    if (flashTimer.current) return;
    let on = false;
    flashTimer.current = setInterval(() => {
      on = !on;
      document.title = on
        ? `(${unseen.current}) 🔔 New chat message`
        : originalTitle.current;
    }, 1000);
  };

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
    const onGesture = () => primeAudio();
    window.addEventListener('click', onGesture, { once: true });
    const onVisible = () => { if (!document.hidden) stopFlash(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('click', onGesture);
      document.removeEventListener('visibilitychange', onVisible);
      stopFlash();
    };
  }, []);

  useEffect(() => {
    let stopped = false;

    const poll = async () => {
      try {
        const { data } = await api.get('/website-chat/summary');
        // Track whether any conversation is still waiting to be opened, so the
        // 15s re-alert below keeps sounding until a human picks it up.
        hasUnread.current = (data.sessions_with_unread || 0) > 0;
        const id = data.latest_inbound_id;
        if (lastId.current === undefined) {
          lastId.current = id || null; // baseline on first poll — don't alert
          return;
        }
        if (id && id !== lastId.current) {
          lastId.current = id;
          const preview = data.latest_preview || 'New website chat message';
          if ('Notification' in window && Notification.permission === 'granted') {
            const n = new Notification('New website chat message', {
              body: preview,
              tag: 'website-chat',
              icon: '/favicon.ico',
            });
            n.onclick = () => { window.focus(); navigate('/website-chat'); n.close(); };
          }
          playDing();
          // Persistent in-app popup that stays until the user clicks it.
          setPopup({ preview });
          // Flash the tab title while the CRM is in a background tab
          if (document.hidden) {
            unseen.current += 1;
            originalTitle.current = originalTitle.current || document.title;
            startFlash();
          }
        }
      } catch { /* noop */ }
    };

    poll();
    const t = setInterval(() => { if (!stopped) poll(); }, 5000);
    // Re-alert every 15s while a conversation is still unopened.
    const reAlert = setInterval(() => {
      if (!stopped && hasUnread.current) playDing();
    }, 15000);
    const onRefresh = () => poll();
    window.addEventListener('website-chat-refresh', onRefresh);
    return () => { stopped = true; clearInterval(t); clearInterval(reAlert); window.removeEventListener('website-chat-refresh', onRefresh); };
  }, [navigate]);

  const openChat = () => { setPopup(null); stopFlash(); navigate('/website-chat'); };

  if (!popup) return null;

  return (
    <div
      className="fixed bottom-6 right-6 z-[9999] w-80 max-w-[calc(100vw-3rem)] rounded-xl bg-white border-2 border-blue-500 shadow-2xl animate-in slide-in-from-bottom-4"
      data-testid="chat-notification-popup"
      role="alert"
    >
      <div className="flex items-start gap-3 p-4">
        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
          <MessagesSquare className="w-5 h-5 text-blue-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-900 text-sm">New website chat message</p>
          <p className="text-sm text-slate-500 mt-0.5 line-clamp-2">{popup.preview}</p>
          <button
            onClick={openChat}
            className="mt-3 w-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 rounded-lg transition-colors"
            data-testid="chat-notification-open"
          >
            Open chat
          </button>
        </div>
        <button
          onClick={() => { setPopup(null); }}
          className="text-slate-300 hover:text-slate-500 shrink-0"
          aria-label="Dismiss"
          data-testid="chat-notification-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default WebsiteChatNotifier;
